import type { Table } from "@tanstack/react-table";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Options du hook useTableSearch
 */
export interface UseTableSearchOptions<TData = Record<string, unknown>> {
  /** Instance TanStack Table */
  table: Table<TData>;
  /** Délai de debounce en ms (défaut: 300) */
  debounceMs?: number;
}

/**
 * Valeurs retournées par useTableSearch
 */
export interface UseTableSearchReturn {
  /** Valeur actuelle de la recherche (immédiat, pas debounce) */
  searchQuery: string;
  /** Met à jour la valeur de recherche (déclenche le debounce) */
  setSearchQuery: (value: string) => void;
  /** Réinitialise la recherche et le filtre global */
  clearSearch: () => void;
}

/**
 * Hook search avec debounce qui synchronise avec le globalFilter TanStack Table.
 *
 * @example
 * ```tsx
 * const { searchQuery, setSearchQuery, clearSearch } = useTableSearch({ table });
 *
 * <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
 * <button onClick={clearSearch}>Réinitialiser</button>
 * ```
 */
export function useTableSearch<TData = Record<string, unknown>>({
  table,
  debounceMs = 300,
}: UseTableSearchOptions<TData>): UseTableSearchReturn {
  const [searchQuery, setSearchQueryState] = useState<string>(
    () => (table.getState().globalFilter as string) ?? "",
  );

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const setSearchQuery = useCallback(
    (value: string) => {
      // Update local state immediately for responsive input
      setSearchQueryState(value);

      // Debounce the sync to table.setGlobalFilter
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        table.setGlobalFilter(value);
        debounceTimerRef.current = null;
      }, debounceMs);
    },
    [table, debounceMs],
  );

  const clearSearch = useCallback(() => {
    // Cancel any pending debounce
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    setSearchQueryState("");
    table.setGlobalFilter("");
  }, [table]);

  return {
    searchQuery,
    setSearchQuery,
    clearSearch,
  };
}
